import {
  BaseActionObject,
  DoneInvokeEvent,
  StateNodeConfig,
  assign
} from "xstate4"
import { Context, Transitions, StudyPlanTargets } from "../types"
import { getServiceErrorMessage } from "../helpers"
import { setSPV2RedirectUrl } from "../studyPlanV2/actions"
import { BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH } from "../studyPlanV2/constants"

export const cancelPlanTransition: StateNodeConfig<
  Context,
  {},
  Transitions,
  BaseActionObject
> = {
  entry: assign<Context>({ loading: true }),
  exit: assign<Context>({ loading: false }),
  invoke: {
    id: "cancelPlan",
    src: "cancelPlan",
    onDone: [
      {
        // If there's a pending program change, go to get_program_courses with the new program
        target: "#StudyPlan.get_program_courses",
        cond: (ctx, _evt) => {
          return !!ctx.pendingProgramChange
        },
        actions: assign(ctx => ({
          program: ctx.pendingProgramChange?.program ?? ctx.program,
          subject: ctx.pendingProgramChange?.subject ?? ctx.subject,
          pendingProgramChange: null,
          currentTarget: null, // Reset target so get_program_courses routes based on role
          isBlank: null,
          seconds: null,
          studyPlan: null,
          hasUnsavedStudyPlanChanges: false
        }))
      },
      {
        target: `${BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH}`,
        actions: assign((ctx, evt) => {
          return {
            // If there's a pending navigation URL, set externalRedirectUrl
            ...(ctx.pendingNavigationUrl
              ? {
                  externalRedirectUrl: {
                    path: ctx.pendingNavigationUrl,
                    action: "push" as const
                  }
                }
              : setSPV2RedirectUrl(
                  ctx,
                  evt,
                  null,
                  StudyPlanTargets.ManageStudyPlans
                )),
            isBlank: null,
            seconds: null,
            studyPlan: null,
            pendingProgramChange: null,
            pendingNavigationUrl: null
          }
        }),
        cond: (ctx, _evt) => {
          const isAdmin =
            ctx.selfRefScope.scope === "DISTRICT" ||
            ctx.selfRefScope.scope === "SCHOOL"
          return (
            !ctx.pendingProgramChange && isAdmin && ctx.isStudyPlanV2Enabled
          )
        }
      },
      {
        target: "#StudyPlan.study_plans_overview",
        cond: (ctx, _evt) => {
          const isAdmin =
            ctx.selfRefScope.scope === "DISTRICT" ||
            ctx.selfRefScope.scope === "SCHOOL"
          return !ctx.pendingProgramChange && isAdmin
        },
        actions: assign((ctx, _evt_) => {
          return {
            // If there's a pending navigation URL, set externalRedirectUrl
            externalRedirectUrl: ctx.pendingNavigationUrl
              ? {
                  path: ctx.pendingNavigationUrl,
                  action: "push" as const
                }
              : undefined,
            isBlank: null,
            seconds: null,
            studyPlan: null,
            pendingProgramChange: null,
            pendingNavigationUrl: null
          }
        })
      },
      {
        target: "#StudyPlan.teacher_view",
        cond: (ctx, _evt) => {
          const isTeacher = ctx.selfRefScope.scope === "TEACHER"
          return !ctx.pendingProgramChange && isTeacher
        },
        actions: assign((ctx, _evt_) => {
          return {
            // If there's a pending navigation URL, set externalRedirectUrl
            externalRedirectUrl: ctx.pendingNavigationUrl
              ? {
                  path: ctx.pendingNavigationUrl,
                  action: "push" as const
                }
              : undefined,
            isBlank: null,
            seconds: null,
            studyPlan: null,
            pendingProgramChange: null,
            pendingNavigationUrl: null
          }
        })
      }
    ],
    onError: [
      {
        target: `${BASE_STUDY_PLAN_V2_ADMIN_MANAGE_PATH}`,
        actions: assign<Context, DoneInvokeEvent<any>>((ctx, evt) => ({
          pendingProgramChange: null,
          ...setSPV2RedirectUrl(
            ctx,
            evt,
            { action: { action: "replace" } },
            StudyPlanTargets.ManageStudyPlans
          ),
          snackbar: {
            message: getServiceErrorMessage("cancelPlan", {
              errorReason: evt.data.message,
              messageData: ["cancel"]
            }),
            withUndo: false,
            error: true
          }
        })),
        cond: (ctx, _evt) => {
          return (
            (ctx.selfRefScope.scope === "DISTRICT" ||
              ctx.selfRefScope.scope === "SCHOOL") &&
            ctx.isStudyPlanV2Enabled
          )
        }
      },
      {
        target: "#StudyPlan.study_plans_overview",
        cond: (ctx, _evt) =>
          ctx.selfRefScope.scope === "DISTRICT" ||
          ctx.selfRefScope.scope === "SCHOOL",
        actions: assign<Context, DoneInvokeEvent<any>>({
          pendingProgramChange: null,
          snackbar: (_ctx, evt) => ({
            message: getServiceErrorMessage("cancelPlan", {
              errorReason: evt.data.message,
              messageData: ["cancel"]
            }),
            withUndo: false,
            error: true
          })
        })
      },
      {
        target: "#StudyPlan.teacher_view",
        cond: (ctx, _evt) => ctx.selfRefScope.scope === "TEACHER",
        actions: assign<Context, DoneInvokeEvent<any>>({
          pendingProgramChange: null,
          snackbar: (_ctx, evt) => ({
            message: getServiceErrorMessage("cancelPlan", {
              errorReason: evt.data.message,
              messageData: ["cancel"]
            }),
            withUndo: false,
            error: true
          })
        })
      }
    ]
  }
}
